import { isSubmissionsExportMetaKey } from "../forms/submissionsExport";
import type { SubmissionsExportMetaKey } from "../../types/submission";
import type {
  ExcelSyncPackageResponse,
  SubmissionSync,
  SubmissionSyncAuth,
  SubmissionSyncColumns,
  SubmissionSyncCreateResponse,
  SubmissionSyncDelta,
  SubmissionSyncDeltaRow,
  SubmissionSyncSidecar,
} from "../../types/submissionSync";

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function readBool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function readStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => readString(item))
    .filter((item): item is string => item != null);
}

function readIdList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => readNumber(item))
    .filter((item): item is number => item != null);
}

function readMetaKeys(value: unknown): SubmissionsExportMetaKey[] {
  return readStringList(value).filter(isSubmissionsExportMetaKey);
}

function readEndpoints(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const endpoints: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const url = readString(raw);
    if (url) endpoints[key] = url;
  }
  return endpoints;
}

function readAuth(value: unknown, hasToken: boolean): SubmissionSyncAuth {
  if (value === "token" || value === "chm") return value;
  return hasToken ? "token" : "chm";
}

export function normalizeSubmissionSyncColumns(
  raw: unknown,
): SubmissionSyncColumns {
  const item =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const orderIds = readIdList(item.orderIds ?? item.order_ids);
  return {
    headers: readStringList(item.headers),
    fieldIds: readIdList(item.fieldIds ?? item.field_ids),
    metaKeys: readMetaKeys(item.metaKeys ?? item.meta_keys),
    orderIds:
      item.orderIds === null || item.order_ids === null
        ? null
        : orderIds.length > 0
          ? orderIds
          : null,
    staffColumnIds: readIdList(item.staffColumnIds ?? item.staff_column_ids),
  };
}

export function normalizeSubmissionSync(raw: unknown): SubmissionSync | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const id = readNumber(item.id);
  const formId = readNumber(item.formId ?? item.form_id);
  if (id == null || formId == null) return null;

  return {
    id,
    formId,
    token: readString(item.token),
    expiresAt: readString(item.expiresAt ?? item.expires_at),
    revokedAt: readString(item.revokedAt ?? item.revoked_at),
    usable: readBool(
      item.usable,
      item.revokedAt == null && item.revoked_at == null,
    ),
    includeStaffMarks: readBool(
      item.includeStaffMarks ?? item.include_staff_marks,
    ),
    columns: normalizeSubmissionSyncColumns(item.columns),
  };
}

export function normalizeSubmissionSyncList(raw: unknown): SubmissionSync[] {
  const rows = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? Array.isArray((raw as { data?: unknown }).data)
        ? (raw as { data: unknown[] }).data
        : Array.isArray((raw as { syncs?: unknown }).syncs)
          ? (raw as { syncs: unknown[] }).syncs
          : (raw as { sync?: unknown }).sync
            ? [(raw as { sync: unknown }).sync]
            : []
      : [];

  return rows
    .map(normalizeSubmissionSync)
    .filter((item): item is SubmissionSync => item != null);
}

export function normalizeSubmissionSyncSidecar(
  raw: unknown,
): SubmissionSyncSidecar | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const formId = readNumber(item.formId ?? item.form_id);
  if (formId == null) return null;

  const token = readString(item.token);
  const auth = readAuth(item.auth, token != null);
  if (auth === "token" && !token) return null;

  const cursorRaw =
    item.cursor && typeof item.cursor === "object"
      ? (item.cursor as Record<string, unknown>)
      : {};

  return {
    version: readNumber(item.version) ?? (auth === "chm" ? 2 : 1),
    auth,
    apiBase: readString(item.apiBase ?? item.api_base) ?? "",
    formId,
    includeStaffMarks: readBool(
      item.includeStaffMarks ?? item.include_staff_marks,
    ),
    columns: normalizeSubmissionSyncColumns(item.columns),
    cursor: {
      afterId: readNumber(cursorRaw.afterId ?? cursorRaw.after_id) ?? 0,
    },
    endpoints: readEndpoints(item.endpoints),
    authHint: readString(item.authHint ?? item.auth_hint) ?? undefined,
    token: auth === "token" ? (token ?? undefined) : undefined,
    expiresAt: readString(item.expiresAt ?? item.expires_at),
  };
}

export function normalizeExcelSyncPackageResponse(
  raw: unknown,
): ExcelSyncPackageResponse {
  if (!raw || typeof raw !== "object") {
    throw new Error("Некорректный ответ пакета Excel-синка");
  }
  const item = raw as Record<string, unknown>;
  const sidecar = normalizeSubmissionSyncSidecar(item.sidecar ?? item);
  if (!sidecar) {
    throw new Error("В пакете Excel-синка нет sidecar");
  }
  return { sidecar };
}

export function normalizeSubmissionSyncCreateResponse(
  raw: unknown,
): SubmissionSyncCreateResponse {
  if (!raw || typeof raw !== "object") {
    throw new Error("Некорректный ответ создания Excel-синка");
  }
  const item = raw as Record<string, unknown>;
  const sync = normalizeSubmissionSync(item.sync ?? item);
  const sidecar = normalizeSubmissionSyncSidecar(item.sidecar);
  if (!sync || !sidecar) {
    throw new Error("Ответ создания Excel-синка без sync или sidecar");
  }
  return { sync, sidecar };
}

function normalizeDeltaRow(raw: unknown): SubmissionSyncDeltaRow | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const id = readNumber(item.id);
  if (id == null) return null;
  const row = Array.isArray(item.row)
    ? item.row.map((cell) => (cell == null ? "" : String(cell)))
    : [];
  return {
    id,
    createdAt: readString(item.createdAt ?? item.created_at),
    row,
  };
}

export function normalizeSubmissionSyncDelta(
  raw: unknown,
): SubmissionSyncDelta {
  if (!raw || typeof raw !== "object") {
    throw new Error("Некорректный ответ дельты Excel-синка");
  }
  const item = raw as Record<string, unknown>;
  const formId = readNumber(item.formId ?? item.form_id);
  if (formId == null) {
    throw new Error("В дельте Excel-синка нет formId");
  }
  const columnsRaw =
    item.columns && typeof item.columns === "object"
      ? (item.columns as Record<string, unknown>)
      : {};
  const data = Array.isArray(item.data)
    ? item.data
        .map(normalizeDeltaRow)
        .filter((row): row is SubmissionSyncDeltaRow => row != null)
    : [];
  const authRaw = item.auth;

  return {
    formId,
    auth: authRaw === "token" || authRaw === "chm" ? authRaw : undefined,
    expiresAt: readString(item.expiresAt ?? item.expires_at),
    columns: { headers: readStringList(columnsRaw.headers) },
    data,
    afterId: readNumber(item.afterId ?? item.after_id) ?? 0,
    nextAfterId: readNumber(item.nextAfterId ?? item.next_after_id) ?? 0,
    hasMore: readBool(item.hasMore ?? item.has_more),
  };
}

export const SUBMISSION_SYNC_UNAVAILABLE = "Sync-ссылка недоступна";
